import { createBrowserRouter, Outlet } from 'react-router-dom';

import { AppPreview } from '~/components/organisms/AppPreview';
import { AnonymousGuard } from '~/components/utils/AnonymousGuard';
import { AuthenticationGuard } from '~/components/utils/AuthenticationGuard';
import { TrackedUtmCampaign } from '~/components/utils/TrackedUtmCampaign';
import { LoggedInUserChildGuard } from './components/utils/LoggedInUserChildGuard';
import { NewTermsOfServicesChecker } from './components/utils/NewTermsOfServicesChecker';
import {
  AcceptInvitationRoute,
  AppAddUserRoute,
  AppAdsRoute,
  AppAdvancedSettingsRoute,
  AppAndroidBuildRoute,
  AppAssetsSettingsRoute,
  AppCategoryRoute,
  AppCommunityRoute,
  AppContentRoute,
  AppCreateAdRoute,
  AppCreateArticleRoute,
  AppCreateCategoryRoute,
  AppCreateIapPollRoute,
  AppCreateLegalDocumentRoute,
  AppCreateLinkRoute,
  AppCreateLiveStreamRoute,
  AppCreatePollRoute,
  AppCreateUserBadgeRoute,
  AppFormatsSettingsRoute,
  AppGeneralSettingsRoute,
  AppIapPollRoute,
  AppIapPollsRoute,
  AppIosBuildRoute,
  AppLegalDocumentsRoute,
  AppLiveStreamRoute,
  AppLiveStreamsRoute,
  AppLookAndFeelSettingsRoute,
  AppMembersRoute,
  AppMerchandisingRoute,
  AppOnboardingRoute,
  AppPollRoute,
  AppPollsRoute,
  AppPublicPreviewRoute,
  AppRoute,
  AppSearchRoute,
  AppSettingsRoute,
  AppsRoute,
  AppSubscriptionSettingsRoute,
  AppSubscriptionSuccessSettingsRoute,
  AppTabsSettingsRoute,
  AppToolsSmartLinkRoute,
  AppUpdateAdRoute,
  AppUpdateArticleRoute,
  AppUpdateCategoryRoute,
  AppUpdateIapPollRoute,
  AppUpdateLegalDocumentRoute,
  AppUpdateLinkRoute,
  AppUpdateLiveStreamRoute,
  AppUpdatePollRoute,
  AppUpdateUserBadgeRoute,
  AppUpdateUserRoute,
  AppUserBadgesRoute,
  AppUserContentReviewRoute,
  AppUserContentReviewsRoute,
  AppUsersRoute,
  AppWPLeaderboardRoute,
  AppWPPlaylistsRoute,
  CreateAppRoute,
  CreateOrganizationRoute,
  ErrorRoute,
  HomeRoute,
  InvitationDeclinedRoute,
  InvitationRoute,
  LogInRoute,
  LogOutRoute,
  NewSessionRoute,
  OrganizationAppsRoute,
  OrganizationCreateInvitationRoute,
  OrganizationInvitationsRoute,
  OrganizationOnboardingRoute,
  OrganizationRoute,
  OrganizationSettingsRoute,
  OrganizationUserRoute,
  OrganizationUsersRoute,
  PrepareAndroidPackageRoute,
  PrepareIosPackageRoute,
  RegionHomeRoute,
  RegionOnboardingRoute,
  ResetPasswordRoute,
  SessionsRoute,
  SignUpRoute,
  SwitchSessionRoute,
  UpdateUserRoute,
} from './routes';
import { AppStartupScreenSettingsRoute } from './routes/AppStartupScreenSettingsRoute';

export const router = createBrowserRouter(
  [
    {
      path: '',
      element: <HomeRoute />,
    },
    {
      path: '/sessions',
      element: <SessionsRoute />,
    },
    {
      path: '/sessions/new',
      element: <NewSessionRoute />,
    },
    {
      path: '/sessions/switch/:sessionId',
      element: <SwitchSessionRoute />,
    },
    {
      path: '/logout',
      element: <LogOutRoute />,
    },
    {
      path: '/login',
      element: <AnonymousGuard component={<LogInRoute />} />,
    },
    {
      path: '/reset-password',
      element: <AnonymousGuard component={<ResetPasswordRoute />} />,
    },
    {
      path: '/signup',
      element: <AnonymousGuard component={<SignUpRoute />} />,
    },
    {
      path: '/:appRegion',
      children: [
        {
          errorElement: <ErrorRoute />,
          element: <TrackedUtmCampaign component={<Outlet />} />,
          children: [
            {
              path: 'invitations/:invitationId',
              element: <InvitationRoute />,
            },
            {
              path: 'invitation-declined',
              element: <InvitationDeclinedRoute />,
            },
            {
              path: 'preview/:appUri',
              element: <AppPublicPreviewRoute />,
            },
            {
              path: '',
              element: (
                <>
                  <LoggedInUserChildGuard>
                    <NewTermsOfServicesChecker />
                  </LoggedInUserChildGuard>
                  <AuthenticationGuard component={<Outlet />} />
                </>
              ),
              children: [
                {
                  path: '',
                  element: <RegionHomeRoute />,
                },
                {
                  path: 'onboarding',
                  element: <RegionOnboardingRoute />,
                },
                {
                  path: 'apps/:appId/onboarding',
                  element: <AppOnboardingRoute />,
                },
                {
                  path: 'organizations/:organizationId/onboarding',
                  element: <OrganizationOnboardingRoute />,
                },
                {
                  path: 'apps',
                  element: <AppsRoute />,
                },
                {
                  path: 'apps/create',
                  element: <CreateAppRoute />,
                },
                {
                  path: 'apps/:appId/search',
                  element: <AppSearchRoute />,
                },
                {
                  path: 'apps/:appId',
                  element: (
                    <>
                      <Outlet />
                      <AppPreview thumb />
                    </>
                  ),
                  children: [
                    {
                      path: '',
                      element: <AppRoute />,
                    },
                    {
                      path: 'content',
                      element: <AppContentRoute />,
                    },
                    {
                      path: 'categories/:categoryId?',
                      element: <AppCategoryRoute />,
                    },
                    {
                      path: 'articles/:categoryId?/create',
                      element: <AppCreateArticleRoute />,
                    },
                    {
                      path: 'articles/:articleId/update',
                      element: <AppUpdateArticleRoute />,
                    },
                  ],
                },
                {
                  path: 'apps/:appId',
                  children: [
                    {
                      element: (
                        <>
                          <Outlet />,
                          <AppPreview thumb />
                        </>
                      ),
                      children: [
                        {
                          path: 'settings/lookandfeel',
                          element: <AppLookAndFeelSettingsRoute />,
                        },
                        {
                          path: 'settings/tabs',
                          element: <AppTabsSettingsRoute />,
                        },
                      ],
                    },
                    {
                      path: 'categories/:categoryId?/create',
                      element: <AppCreateCategoryRoute />,
                    },
                    {
                      path: 'categories/:categoryId?/update',
                      element: <AppUpdateCategoryRoute />,
                    },
                    {
                      path: 'links/:categoryId?/create',
                      element: <AppCreateLinkRoute />,
                    },
                    {
                      path: 'links/:categoryId/update',
                      element: <AppUpdateLinkRoute />,
                    },
                    {
                      path: 'settings',
                      element: <AppSettingsRoute />,
                    },
                    {
                      path: 'settings/general',
                      element: <AppGeneralSettingsRoute />,
                    },
                    {
                      path: 'settings/formats',
                      element: <AppFormatsSettingsRoute />,
                    },
                    {
                      path: 'settings/assets',
                      element: <AppAssetsSettingsRoute />,
                    },
                    {
                      path: 'settings/users',
                      element: <AppUsersRoute />,
                    },
                    {
                      path: 'settings/users/add',
                      element: <AppAddUserRoute />,
                    },
                    {
                      path: 'settings/users/:userId',
                      element: <AppUpdateUserRoute />,
                    },
                    {
                      path: 'settings/legalDocuments',
                      element: <AppLegalDocumentsRoute />,
                    },
                    {
                      path: 'settings/legalDocuments/create',
                      element: <AppCreateLegalDocumentRoute />,
                    },
                    {
                      path: 'settings/legalDocuments/:legalDocumentId',
                      element: <AppUpdateLegalDocumentRoute />,
                    },
                    {
                      path: 'settings/advanced',
                      element: <AppAdvancedSettingsRoute />,
                    },
                    {
                      path: 'settings/subscription',
                      element: <AppSubscriptionSettingsRoute />,
                    },
                    {
                      path: 'settings/subscription/success',
                      element: <AppSubscriptionSuccessSettingsRoute />,
                    },
                    {
                      path: 'settings/startupScreen',
                      element: <AppStartupScreenSettingsRoute />,
                    },
                    {
                      path: 'builds/ios/prepare',
                      element: <PrepareIosPackageRoute />,
                    },
                    {
                      path: 'builds/android/prepare',
                      element: <PrepareAndroidPackageRoute />,
                    },
                    {
                      path: 'builds/ios',
                      element: <AppIosBuildRoute />,
                    },
                    {
                      path: 'builds/android',
                      element: <AppAndroidBuildRoute />,
                    },
                    {
                      path: 'tools/smartLink',
                      element: <AppToolsSmartLinkRoute />,
                    },
                    {
                      path: 'moderation',
                      element: <AppUserContentReviewsRoute />,
                    },
                    {
                      path: 'moderation/:userContentId',
                      element: <AppUserContentReviewRoute />,
                    },
                    {
                      path: 'community',
                      element: <AppCommunityRoute />,
                    },
                    {
                      path: 'members',
                      element: <AppMembersRoute />,
                    },
                    {
                      path: 'liveStreams',
                      element: <AppLiveStreamsRoute />,
                    },
                    {
                      path: 'liveStreams/create',
                      element: <AppCreateLiveStreamRoute />,
                    },
                    {
                      path: 'liveStreams/:liveStreamId',
                      element: <AppLiveStreamRoute />,
                    },
                    {
                      path: 'liveStreams/:liveStreamId/update',
                      element: <AppUpdateLiveStreamRoute />,
                    },
                    {
                      path: 'ads',
                      element: <AppAdsRoute />,
                    },
                    {
                      path: 'ads/create',
                      element: <AppCreateAdRoute />,
                    },
                    {
                      path: 'ads/:adId/update',
                      element: <AppUpdateAdRoute />,
                    },
                    {
                      path: 'badges',
                      element: <AppUserBadgesRoute />,
                    },
                    {
                      path: 'badges/create',
                      element: <AppCreateUserBadgeRoute />,
                    },
                    {
                      path: 'badges/:userBadgeId',
                      element: <AppUpdateUserBadgeRoute />,
                    },
                    {
                      path: 'polls',
                      element: <AppPollsRoute />,
                    },
                    {
                      path: 'polls/create',
                      element: <AppCreatePollRoute />,
                    },
                    {
                      path: 'polls/:pollId',
                      element: <AppPollRoute />,
                    },
                    {
                      path: 'polls/:pollId/update',
                      element: <AppUpdatePollRoute />,
                    },
                    {
                      path: 'iapPolls',
                      element: <AppIapPollsRoute />,
                    },
                    {
                      path: 'iapPolls/create',
                      element: <AppCreateIapPollRoute />,
                    },
                    {
                      path: 'iapPolls/:iapPollId',
                      element: <AppIapPollRoute />,
                    },
                    {
                      path: 'iapPolls/:iapPollId/update',
                      element: <AppUpdateIapPollRoute />,
                    },
                    {
                      path: 'playlists',
                      element: <AppWPPlaylistsRoute />,
                    },
                    {
                      path: 'leaderboard',
                      element: <AppWPLeaderboardRoute />,
                    },
                    {
                      path: 'merch',
                      element: (
                        <>
                          <AppMerchandisingRoute />
                          <AppPreview thumb />
                        </>
                      ),
                    },
                  ],
                },
                {
                  path: 'organizations',
                  children: [
                    {
                      path: 'create',
                      element: <CreateOrganizationRoute />,
                    },
                    {
                      path: ':organizationId',
                      children: [
                        {
                          path: '',
                          element: <OrganizationRoute />,
                        },
                        {
                          path: 'apps',
                          element: <OrganizationAppsRoute />,
                        },
                        {
                          path: 'apps/create',
                          element: <CreateAppRoute />,
                        },
                        {
                          path: 'users',
                          element: <OrganizationUsersRoute />,
                        },
                        {
                          path: 'users/:userId',
                          element: <OrganizationUserRoute />,
                        },
                        {
                          path: 'invitations',
                          element: <OrganizationInvitationsRoute />,
                        },
                        {
                          path: 'invitations/create',
                          element: <OrganizationCreateInvitationRoute />,
                        },
                        {
                          path: 'invitations/:invitationId/accept',
                          element: <AcceptInvitationRoute />,
                        },
                        {
                          path: 'settings',
                          element: <OrganizationSettingsRoute />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'me/update',
                  element: <UpdateUserRoute />,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    basename: __BASENAME__ ? `/${__BASENAME__}/` : undefined,
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
